import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Paper, TextField,
  InputAdornment, List, ListItemButton, ListItemText, Chip, Divider, Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  MenuBook as DocIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import { documentationApi } from '../../../services/apiClient';

// Markdown container styles (matching DocumentViewer)
const markdownSx = {
  '& h1': {
    fontSize: '2rem',
    fontWeight: 'bold',
    mt: 3,
    mb: 2,
    borderBottom: '2px solid',
    borderColor: 'divider',
    pb: 1
  },
  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    mt: 2.5,
    mb: 1.5
  },
  '& h3': {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    mt: 2,
    mb: 1
  },
  '& p': {
    mb: 2,
    lineHeight: 1.7
  },
  '& code': {
    backgroundColor: 'action.hover',
    padding: '2px 6px',
    borderRadius: 1,
    fontSize: '0.875rem',
    fontFamily: 'monospace'
  },
  '& pre': {
    backgroundColor: 'action.hover',
    padding: 2,
    borderRadius: 1,
    overflow: 'auto',
    mb: 2,
    '& code': {
      backgroundColor: 'transparent',
      padding: 0
    }
  },
  '& ul, & ol': {
    mb: 2,
    pl: 3
  },
  '& li': {
    mb: 0.5
  },
  '& blockquote': {
    borderLeft: '4px solid',
    borderColor: 'primary.main',
    pl: 2,
    ml: 0,
    fontStyle: 'italic',
    color: 'text.secondary'
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    mb: 2
  },
  '& th': {
    backgroundColor: 'action.hover',
    fontWeight: 'bold',
    padding: 1,
    border: '1px solid',
    borderColor: 'divider'
  },
  '& td': {
    padding: 1,
    border: '1px solid',
    borderColor: 'divider'
  },
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 1,
    mt: 2,
    mb: 2
  }
};

// Extract headings from markdown for table of contents
const extractHeadings = (markdown) => {
  if (!markdown) return [];
  const headingRegex = /^(#{2,3})\s+(.+)$/gm;
  const headings = [];
  let match;
  while ((match = headingRegex.exec(markdown)) !== null) {
    headings.push({
      level: match[1].length,
      text: match[2].trim(),
      id: match[2].trim().toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
    });
  }
  return headings;
};

const DocumentationTab = ({ service }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableDocs, setAvailableDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeDocTab, setActiveDocTab] = useState(0);

  const serviceName = service?.name || '';

  // Fetch list of available device scripts to check for multiple docs
  const fetchDocList = useCallback(async () => {
    try {
      const response = await documentationApi.getDeviceScripts();
      const scripts = response.data?.scripts || response.data?.data || [];
      // Find scripts matching this service — try exact match, then with prefix, then suffix match
      const matching = scripts.find(s =>
        s.folder === serviceName ||
        s.folder === `service_${serviceName}` ||
        s.folder === `caro_${serviceName}` ||
        s.folder?.endsWith(`_${serviceName}`)
      );
      if (matching && matching.docs && matching.docs.length > 1) {
        setAvailableDocs(matching.docs);
        return matching.docs;
      }
      return [];
    } catch {
      return [];
    }
  }, [serviceName]);

  // Fetch documentation content
  const fetchDoc = useCallback(async (file = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentationApi.getDeviceScriptDoc(serviceName, file);
      const docContent = response.data?.doc?.content || response.data?.data?.content || response.data?.content || '';
      if (typeof docContent === 'string' && docContent.trim()) {
        setContent(docContent);
      } else {
        setContent('');
        setError('No documentation available for this service.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No documentation available for this service.');
      } else {
        setError('Failed to load documentation.');
      }
      setContent('');
    } finally {
      setLoading(false);
    }
  }, [serviceName]);

  useEffect(() => {
    const init = async () => {
      const docs = await fetchDocList();
      if (docs.length > 1) {
        // Multiple docs available - load the first one
        setSelectedDoc(docs[0]);
        await fetchDoc(docs[0]);
      } else {
        // Single doc or none
        await fetchDoc();
      }
    };
    if (serviceName) {
      init();
    } else {
      setLoading(false);
      setError('No service name provided.');
    }
  }, [serviceName, fetchDocList, fetchDoc]);

  // Handle doc tab change for multi-doc services
  const handleDocTabChange = async (event, newValue) => {
    setActiveDocTab(newValue);
    const file = availableDocs[newValue];
    setSelectedDoc(file);
    await fetchDoc(file);
  };

  // Table of contents
  const headings = useMemo(() => extractHeadings(content), [content]);

  // Search/filter - highlight matches in content
  const highlightedContent = useMemo(() => {
    if (!searchQuery.trim() || !content) return content;
    return content;
  }, [content, searchQuery]);

  // Check if content matches search
  const hasSearchMatch = useMemo(() => {
    if (!searchQuery.trim()) return true;
    return content.toLowerCase().includes(searchQuery.toLowerCase());
  }, [content, searchQuery]);

  // Custom renderer to highlight search matches
  const components = useMemo(() => {
    if (!searchQuery.trim()) return {};
    const query = searchQuery.toLowerCase();
    return {
      p: ({ children, ...props }) => {
        const text = typeof children === 'string' ? children : '';
        if (text.toLowerCase().includes(query)) {
          const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
          return (
            <p {...props}>
              {parts.map((part, i) =>
                part.toLowerCase() === query ? (
                  <mark key={i} style={{ backgroundColor: '#fff176', padding: '1px 2px', borderRadius: 2 }}>
                    {part}
                  </mark>
                ) : part
              )}
            </p>
          );
        }
        return <p {...props}>{children}</p>;
      },
      li: ({ children, ...props }) => {
        const text = typeof children === 'string' ? children : '';
        if (text.toLowerCase().includes(query)) {
          const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
          return (
            <li {...props}>
              {parts.map((part, i) =>
                part.toLowerCase() === query ? (
                  <mark key={i} style={{ backgroundColor: '#fff176', padding: '1px 2px', borderRadius: 2 }}>
                    {part}
                  </mark>
                ) : part
              )}
            </li>
          );
        }
        return <li {...props}>{children}</li>;
      }
    };
  }, [searchQuery]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading documentation...
        </Typography>
      </Box>
    );
  }

  if (error && !content) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <DocIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Documentation Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <Chip
          label={serviceName}
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 3 }}>
      {/* Table of Contents Sidebar */}
      {headings.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            width: 240,
            flexShrink: 0,
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 200px)',
            overflow: 'auto'
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Table of Contents
          </Typography>
          <List dense disablePadding>
            {headings.map((heading, index) => (
              <ListItemButton
                key={index}
                sx={{
                  py: 0.5,
                  pl: heading.level === 3 ? 2 : 0,
                  borderRadius: 0.5,
                  minHeight: 'auto'
                }}
                onClick={() => {
                  const el = document.getElementById(heading.id);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <ListItemText
                  primary={heading.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontSize: heading.level === 3 ? '0.775rem' : '0.85rem',
                    fontWeight: heading.level === 2 ? 600 : 400,
                    color: 'text.secondary',
                    noWrap: true
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Search Input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search documentation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />

        {/* Doc tabs for multi-doc services */}
        {availableDocs.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Tabs
              value={activeDocTab}
              onChange={handleDocTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {availableDocs.map((doc, index) => (
                <Tab
                  key={index}
                  label={doc}
                  icon={<ArticleIcon />}
                  iconPosition="start"
                  sx={{ textTransform: 'none' }}
                />
              ))}
            </Tabs>
            <Divider />
          </Box>
        )}

        {/* Search no-match notice */}
        {searchQuery && !hasSearchMatch && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No matches found for "{searchQuery}"
          </Alert>
        )}

        {/* Markdown Content */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            ...markdownSx
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
              ...components,
              h2: ({ children, ...props }) => {
                const id = typeof children === 'string'
                  ? children.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
                  : '';
                return <h2 id={id} {...props}>{children}</h2>;
              },
              h3: ({ children, ...props }) => {
                const id = typeof children === 'string'
                  ? children.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
                  : '';
                return <h3 id={id} {...props}>{children}</h3>;
              }
            }}
          >
            {highlightedContent}
          </ReactMarkdown>
        </Paper>
      </Box>
    </Box>
  );
};

export default DocumentationTab;
